import { TablePaginationConfig } from 'antd';
import { ColumnType } from 'antd/es/table';
import type {
  ColumnsType,
  FilterValue,
  SorterResult,
  TableCurrentDataSource,
} from 'antd/es/table/interface';
import {
  ColumnSortDirections,
  antdTableSortToDirection,
} from '../../components/table/TableColumnSort';
import { getPagination } from './getPagination';
import { getOrderBy } from './paginationUrlColumns';
import { IUseSetPaginationUrlQuery } from './paginationUrlQuery';

export interface IHandlePaginationChange<
  TData,
  TParams extends IBasePaginable,
> {
  defaultQuery: TParams;
  query: TParams;
  setQuery: IUseSetPaginationUrlQuery<TParams>;
  getFilters?: (
    filters: Record<string, FilterValue | null>,
    ifDefined: IfDefinedFn,
  ) => Partial<TParams>;
  /**
   * Obbligatorio poiché serve per capire quale ordinamento è stato selezionato;
   * se non viene passato, `sortDataIndex` non può essere considerato.
   */
  columns: ColumnsType<TData>;
}

const ifDefined = (value: any, transform: (value: any) => any) => {
  if (value == null) return null;
  return transform(value);
};

type IfDefinedFn = typeof ifDefined;

interface IBasePaginable {
  ordinamenti?: any;
  pagina?: number;
  limite?: number;
}

export function handlePaginationChange<TData, TParams extends IBasePaginable>(
  options: IHandlePaginationChange<TData, TParams>,
) {
  const {
    columns,
    defaultQuery,
    query,
    setQuery,
    getFilters = () => ({}),
  } = options;
  const defaultSortField = getOrderBy(defaultQuery);

  return function onChange(
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<TData> | Array<SorterResult<TData>>,
    extra?: TableCurrentDataSource<TData>,
  ) {
    const { order, field } = sorter as SorterResult<TData>;
    const fieldColumn = columns?.find(column =>
      'dataIndex' in column ? column.dataIndex === field : false,
    ) as ColumnType<TData>;
    let sort = (fieldColumn?.sortDataIndex ?? field) as string | null;
    let direction = antdTableSortToDirection(order);

    // Se non è stato impostato un ordinamento, utilizzo quello di default
    if (sort == null || sort === defaultSortField) {
      sort = getOrderBy(defaultQuery);
      direction = direction ?? ColumnSortDirections.ascend;
    }

    // Se non stiamo cambiando pagina, vuol dire che ho applicato un filtro
    // o un ordinamento. In ogni caso voglio tornare alla prima pagina.
    const shouldResetPage =
      (pagination.current ?? 1) - 1 === Number(query.pagina);

    const nextQuery: any = {
      ...query,
      ...getPagination({
        page: shouldResetPage ? 0 : pagination.current,
        pageSize: pagination.pageSize,
        sort,
        direction,
      }),
      ...getFilters(filters, ifDefined),
    };

    setQuery(nextQuery);
  };
}
